import { useSyncExternalStore } from "react";
import openSocket from "../../services/socket-io";
import toastError from "../../errors/toastError";

import api from "../../services/api";

const reducer = (state, action) => {
	if (action.type === "LOAD_WHATSAPPS") {
		const whatsApps = action.payload;

		return [...whatsApps];
	}

	if (action.type === "UPDATE_WHATSAPPS") {
		const whatsApp = action.payload;
		const whatsAppIndex = state.findIndex(s => s.id === whatsApp.id);

		if (whatsAppIndex !== -1) {
			// Mesclar, não substituir: nem todo evento traz a conexão inteira.
			// Trocar a padrão emite a conexão ANTIGA sem as filas, e a linha
			// dela passava a dizer "sem fila" até alguém recarregar. Quem manda
			// um campo explicitamente (inclusive lista vazia) continua mandando.
			const anterior = state[whatsAppIndex];
			const mesclado = { ...anterior, ...whatsApp };

			// `canal` não é campo gravado: é a projeção que a LISTAGEM deriva do
			// provider, com as capacidades (arquivar, bloquear, reagir...). O
			// socket não a manda, então mesclar carregaria a projeção ANTIGA
			// para o provider NOVO — e a tela ofereceria bloco que o backend
			// recusa, ou esconderia o que passou a existir. Provider diferente,
			// projeção descartada: sem ela a tela cai no padrão, e a próxima
			// listagem traz a verdadeira.
			if (whatsApp.apiProvider && whatsApp.apiProvider !== anterior.apiProvider) {
				delete mesclado.canal;
			}

			state[whatsAppIndex] = mesclado;
			return [...state];
		} else {
			return [whatsApp, ...state];
		}
	}

	if (action.type === "UPDATE_SESSION") {
		const whatsApp = action.payload;
		const whatsAppIndex = state.findIndex(s => s.id === whatsApp.id);

		if (whatsAppIndex !== -1) {
			state[whatsAppIndex].status = whatsApp.status;
			state[whatsAppIndex].updatedAt = whatsApp.updatedAt;
			state[whatsAppIndex].qrcode = whatsApp.qrcode;
			state[whatsAppIndex].retries = whatsApp.retries;
			// Desligar e religar chegam por este evento: sem copiar a marca, a
			// lista continuava dizendo "Desligada aqui" numa conexão que já
			// voltou (e o contrário, no desligamento) até alguém dar F5.
			state[whatsAppIndex].stoppedByUser = whatsApp.stoppedByUser;
			// Por que a conexão caiu (ou não subiu). Vem só no evento — não
			// existe no banco —, então some no F5: é o preço de não criar
			// coluna para uma informação que só vale enquanto o erro dura.
			//
			// Ausente NÃO é vazio. Quem administra está nas duas salas e recebe
			// dois eventos do mesmo fato: o da sala de administradores, com o
			// motivo, e o geral, sem ele. Zerar no segundo apagava o texto no
			// mesmo instante em que ele chegava — a correção inteira ficava
			// muda. Só sair de DISCONNECTED limpa: erro velho ao lado de
			// conexão que voltou é pior do que nenhum.
			if (whatsApp.status !== "DISCONNECTED") {
				state[whatsAppIndex].motivo = "";
			} else if (whatsApp.motivo) {
				state[whatsAppIndex].motivo = whatsApp.motivo;
			}
			return [...state];
		} else {
			return [...state];
		}
	}

	if (action.type === "DELETE_WHATSAPPS") {
		const whatsAppId = action.payload;

		const whatsAppIndex = state.findIndex(s => s.id === whatsAppId);
		if (whatsAppIndex !== -1) {
			state.splice(whatsAppIndex, 1);
		}
		return [...state];
	}

	if (action.type === "RESET") {
		return [];
	}
};

// Uma lista de conexões para o app inteiro.
//
// Antes cada chamada do hook buscava `/whatsapp/` e assinava o socket por
// conta própria. Na tela de atendimentos há nove consumidores montados ao
// mesmo tempo (a barra, cada aba da lista, o cabeçalho da conversa...): eram
// nove requisições iguais concorrendo com a busca dos atendimentos na
// abertura da tela, e nove cópias do mesmo estado que podiam divergir.
//
// Sessão vinculada a uma conexão oficial (envio híbrido) fica fora de
// `whatsApps`: ela só envia em nome da oficial, e oferecê-la em lista, filtro
// ou transferência abriria atendimento por um caminho que descarta o que
// chega. A lista inteira continua em `todas` — o reducer trabalha nela, para o
// status da sessão seguir vivo no cartão da oficial (`vinculadas`).
const VAZIO = { todas: [], whatsApps: [], vinculadas: [], loading: true };
let estado = VAZIO;
const ouvintes = new Set();
let consumidores = 0;
let socketDaLista = null;

const publicar = ({ todas, loading }) => {
	estado = {
		todas,
		whatsApps: todas.filter((w) => !w.vinculadaAWhatsappId),
		vinculadas: todas.filter((w) => w.vinculadaAWhatsappId),
		loading,
	};
	ouvintes.forEach((avisar) => avisar());
};

const aplicar = (action) =>
	publicar({ ...estado, todas: reducer(estado.todas, action) });

const assinar = (avisar) => {
	ouvintes.add(avisar);
	consumidores += 1;

	if (consumidores === 1) {
		publicar({ todas: [], loading: true });

		api
			.get("/whatsapp/", { params: { incluirVinculadas: 1 } })
			.then(({ data }) => {
				publicar({ todas: [...data], loading: false });
			})
			.catch((err) => {
				publicar({ ...estado, loading: false });
				toastError(err);
			});

		socketDaLista = openSocket();

		socketDaLista.on("whatsapp", (data) => {
			if (data.action === "update") {
				aplicar({ type: "UPDATE_WHATSAPPS", payload: data.whatsapp });
			}
			if (data.action === "delete") {
				aplicar({ type: "DELETE_WHATSAPPS", payload: data.whatsappId });
			}
		});

		socketDaLista.on("whatsappSession", (data) => {
			if (data.action === "update") {
				aplicar({ type: "UPDATE_SESSION", payload: data.session });
			}
		});

		// Conexão do tempo real que voltou: uma conexão de WhatsApp que caiu ou
		// subiu no intervalo seguiria com a bolinha de antes. Em silêncio, sem
		// o carregando nem toast — quem está na tela não pediu nada.
		socketDaLista.on("resync", () => {
			api
				.get("/whatsapp/", { params: { incluirVinculadas: 1 } })
				.then(({ data }) => {
					if (socketDaLista) publicar({ todas: [...data], loading: false });
				})
				.catch(() => {});
		});
	}

	return () => {
		ouvintes.delete(avisar);
		consumidores -= 1;

		// Último a sair apaga a luz: sem zerar, um login de outra empresa
		// herdaria as conexões da sessão anterior.
		if (consumidores <= 0) {
			consumidores = 0;
			socketDaLista?.disconnect();
			socketDaLista = null;
			estado = VAZIO;
		}
	};
};

const useWhatsApps = () => useSyncExternalStore(assinar, () => estado);

export default useWhatsApps;
